import { window } from "global";
import React, { useEffect, useRef } from "react";
import { useGlobal } from "reactn";
import { RiotAssetsProvider } from "@outplayed/riot-assets";
import AppDataInitializer from "./AppDataInitializer";
import AppLayout from "./AppLayout";
import { useApolloClient, useQuery } from "@apollo/client";
import { GET_USER_STATE } from "@ugg/shared/api/requests/accounts/user-state";
import { useSupabaseAuth } from "@ugg/shared/components/auth/SupabaseAuthProvider";
import { useAutoCreateSupabaseAccount, useSupabaseAccount } from "@ugg/shared/hooks/use-supabase-account";
import { UGG_ACCOUNT_STORAGE_KEY } from "@ugitgud/legos/util/auth/general";
import { UGGAssetsProvider } from "@ugg/shared/components/UGGAssetsProvider";
import { FreestarContainer } from "components/Ads/FreestarContainer";
import { GoogleAnalyticsWrapper } from "./GoogleAnalyticsWrapper";

import { ResponsiveProvider } from "@outplayed/responsive";
import { WINDOW_BREAKPOINTS_RANGE, DEFAULT_BREAKPOINT } from "@ugg/shared/constants/constants";

const UserAuth = () => {
  const client = useApolloClient();
  const { session, status } = useSupabaseAuth();
  const { hasAccount, isResolving } = useSupabaseAccount();
  const [authToken, setAuthToken] = useGlobal("authToken");
  const [, setLoggingIn] = useGlobal("loggingIn");
  const resolvedSignedIn = useRef(null);
  useQuery(GET_USER_STATE, { skip: !authToken });

  useEffect(() => {
    if (status === "loading") return;
    const signedIn = status === "signed-in";
    if (resolvedSignedIn.current !== null && resolvedSignedIn.current !== signedIn) {
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "getState" });
      client.cache.gc();
    }
    resolvedSignedIn.current = signedIn;
  }, [client, status]);

  useEffect(() => {
    if (isResolving) return;
    setAuthToken(hasAccount ? session.access_token : null);
    setLoggingIn(false);
    if (hasAccount) {
      localStorage.setItem(UGG_ACCOUNT_STORAGE_KEY, session.user.id);
    } else {
      localStorage.removeItem(UGG_ACCOUNT_STORAGE_KEY);
    }
  }, [session, hasAccount, isResolving]);

  useAutoCreateSupabaseAccount();

  return null;
};

const App = () => {
  const [allowCssTransition, setAllowCssTransition] = useGlobal("allowCssTransition");

  useEffect(() => {
    // Prevent animation to trigger after hydration
    setAllowCssTransition(true);
  }, []);

  return (
    <ResponsiveProvider breakpoints={WINDOW_BREAKPOINTS_RANGE} defaultBreakpoint={DEFAULT_BREAKPOINT}>
      {window && <UserAuth />}
      {window && <FreestarContainer />}
      {window && <GoogleAnalyticsWrapper />}
      <RiotAssetsProvider staging={process.env.UGG_PUBLIC_RIOT_PATCH_ASSETS === "staging"} ssr>
        <UGGAssetsProvider staging={process.env.UGG_PUBLIC_RIOT_PATCH_ASSETS === "staging"}>
          <AppDataInitializer>
            <AppLayout />
          </AppDataInitializer>
        </UGGAssetsProvider>
      </RiotAssetsProvider>
    </ResponsiveProvider>
  );
};

export default App;
